import { useRef } from 'react'
import { ZIndex } from '@opallabs/gem'
import { useTheme } from '@opallabs/hooks'
import {
  Checkbox,
  InputLabel,
  MultiCheckSelect,
  DotLoader,
} from '@opallabs/lapidary'
import { CreateBoardOption } from '../CreateBoardOption'
import * as styles from '../styles'
import {
  BoardOption,
  AddMomentToBoardModalProps,
  MomentToBoardState,
} from '../types'
import { ValidationMessage } from './ValidationMessage'

type AddMomentToBoardModalContentProps = Pick<
  AddMomentToBoardModalProps,
  'limitOptions' | 'relatedResources'
> &
  Omit<
    MomentToBoardState,
    'boardSelectionChanged' | 'moments' | 'unscheduleMoment'
  > & {
    momentCount: number
    invalidMomentCount: number
  }

export const checkboxLabel = 'Remove moment schedule date'

export const AddMomentToBoardModalContent: React.FC<
  AddMomentToBoardModalContentProps
> = ({
  boardOptions,
  canUnschedule,
  limitOptions,
  hasError,
  initialSelection,
  invalidMomentCount,
  isLoading,
  momentCount,
  setSelectedBoards,
  setUnschedule,
  showEmptyBoardErrorAfterInteraction,
  relatedResources,
  unschedule,
}) => {
  const linkToBoardsRef = useRef<HTMLAnchorElement | null>(null)
  const theme = useTheme()

  if (isLoading) {
    return (
      <DotLoader
        height={styles.getLoaderHeight({
          invalidMomentCount,
          momentCount,
          limitOptions,
        })}
      />
    )
  }

  if (momentCount === invalidMomentCount) {
    return (
      <ValidationMessage
        invalidMomentCount={invalidMomentCount}
        momentCount={momentCount}
        relatedResources={relatedResources}
      />
    )
  }

  return (
    <>
      <ValidationMessage
        invalidMomentCount={invalidMomentCount}
        momentCount={momentCount}
        relatedResources={relatedResources}
      />
      <MultiCheckSelect<BoardOption>
        label="Board Name"
        disableInitialCheckedItems={limitOptions}
        hasError={hasError}
        initialSelection={initialSelection}
        onSelectionChange={showEmptyBoardErrorAfterInteraction}
        onMenuCloseDiff={setSelectedBoards}
        onPersistentOptionSelect={() => linkToBoardsRef.current?.click()}
        options={boardOptions}
        persistentOption={
          !limitOptions || boardOptions.length === 0 ? (
            <CreateBoardOption ref={linkToBoardsRef} />
          ) : null
        }
        placeholder=""
        styleOverrides={{
          width: 336,
          zIndex: ZIndex.Shroud.Popover,
        }}
      />
      {hasError && (
        <p css={styles.errorText(theme)}>
          Add this moment to a board to unschedule
        </p>
      )}
      {!limitOptions && (
        <div css={styles.checkboxGroup(hasError)}>
          <Checkbox
            disabled={!canUnschedule}
            checked={unschedule}
            htmlId="remove-date-checkbox"
            onChange={() => {
              showEmptyBoardErrorAfterInteraction()
              setUnschedule(!unschedule)
            }}
            theme={theme}
          />
          <InputLabel
            inputId="remove-date-checkbox"
            styleOverrides={styles.checkboxLabelOverrides(theme)}
            text={checkboxLabel}
            theme={theme}
          />
        </div>
      )}
    </>
  )
}
